import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Toaster } from "react-hot-toast";
import Header from "./components/layout/Header";
import CategoryGrid from "./components/features/CategoryGrid";
import ErrorBoundary from "./components/common/ErrorBoundary";
import "./styles/index.css";

// Lazy-loaded feature modules
const DataExplorer = lazy(() => import("./components/features/DataExplorer"));
const Reports = lazy(() => import("./components/features/Reports"));
const About = lazy(() => import("./components/features/About"));
const CrossAnalysis = lazy(() => import("./components/features/CrossAnalysis"));
const HomeDashboard = lazy(() => import("./components/features/HomeDashboard"));

// Loading fallback component
const LoadingFallback = ({ text = "Loading..." }) => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-trac-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-sm font-medium text-gray-500 animate-pulse">{text}</p>
    </div>
  </div>
);

// URL-friendly page names mapping
const PAGE_NAMES = {
  home: "home",
  explorer: "explore",
  cross: "cross-analysis",
  reports: "reports",
  about: "about",
};

const PAGE_NAMES_REVERSE = {
  home: "home",
  explore: "explorer",
  "cross-analysis": "cross",
  reports: "reports",
  about: "about",
};

// Parse URL hash to get current state
const parseHash = () => {
  const hash = window.location.hash.slice(1); // Remove #
  if (!hash)
    return {
      page: "home",
      category: null,
      indicator: null,
      view: "map",
      geography: "india",
      residence: "total",
    };

  const params = new URLSearchParams(hash);
  const urlPage = params.get("view") || "home";

  return {
    page: PAGE_NAMES_REVERSE[urlPage] || "home",
    category: params.get("category") || null,
    indicator: params.get("indicator") || null,
    view: params.get("mode") || "map",
    geography: params.get("geo") || "india",
    residence: params.get("res") || "total",
  };
};

// Build URL hash from state
const buildHash = (page, category, indicator, view, geography, residence) => {
  const params = new URLSearchParams();
  params.set("view", PAGE_NAMES[page] || "home");
  if (category) params.set("category", category);
  if (indicator) params.set("indicator", indicator);
  if (view && view !== "map") params.set("mode", view);
  if (geography && geography !== "india") params.set("geo", geography);
  if (residence && residence !== "total") params.set("res", residence);
  return "#" + params.toString();
};

function App() {
  // Initialize state from URL hash
  const initialState = parseHash();
  const [selectedCategory, setSelectedCategory] = useState(
    initialState.category,
  );
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [selectedIndicator, setSelectedIndicator] = useState(
    initialState.indicator,
  );
  const [viewMode, setViewMode] = useState(initialState.view);
  const [geography, setGeography] = useState(initialState.geography);
  const [residence, setResidence] = useState(initialState.residence);

  // Update URL hash when state changes
  useEffect(() => {
    const newHash = buildHash(
      currentPage,
      selectedCategory,
      selectedIndicator,
      viewMode,
      geography,
      residence,
    );
    if (window.location.hash !== newHash) {
      window.history.pushState(null, "", newHash);
    }
  }, [
    currentPage,
    selectedCategory,
    selectedIndicator,
    viewMode,
    geography,
    residence,
  ]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const state = parseHash();
      setCurrentPage(state.page);
      setSelectedCategory(state.category);
      setSelectedIndicator(state.indicator);
      setViewMode(state.view);
      setGeography(state.geography);
      setResidence(state.residence);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleCategorySelect = useCallback(
    (categoryId) => {
      setSelectedCategory(categoryId);
      // Each category should open in default geographic context.
      setGeography("india");
      setResidence("total");
      // Keep indicator if switching categories but clear if none selected
      if (!categoryId) setSelectedIndicator(null);
      // Navigate to per-category explorer (not cross analysis)
      if (currentPage !== "explorer") setCurrentPage("explorer");
    },
    [currentPage],
  );

  // Handler for search bar selection
  const handleSearchSelect = useCallback((selection) => {
    if (selection && selection.indicator && selection.category) {
      setSelectedCategory(selection.category);
      setSelectedIndicator(selection.indicator);
      setGeography("india");
      setResidence("total");
      setCurrentPage("explorer");
    }
  }, []);

  // Handler for indicator selection from DataExplorer
  const handleIndicatorChange = useCallback((indicator) => {
    setSelectedIndicator(indicator);
  }, []);

  // Handler for view mode change
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  // Handler for geography change
  const handleGeographyChange = useCallback((geo) => {
    setGeography(geo);
  }, []);

  // Handler for residence change
  const handleResidenceChange = useCallback((res) => {
    setResidence(res);
  }, []);

  // Navigation handler from Header
  const handleNavigate = useCallback((page) => {
    // Treat any attempt to go to deprecated 'dashboard' as explorer
    const target = page === "dashboard" ? "explorer" : page;
    if (target === "cross" || target === "home") {
      setSelectedCategory(null);
      setSelectedIndicator(null);
    }
    setCurrentPage(target);
  }, []);

  // Logo: landing home with charts; full catalogue is under Explorer
  const handleHome = useCallback(() => {
    setCurrentPage("home");
    setSelectedCategory(null);
    setSelectedIndicator(null);
    setViewMode("map");
    setGeography("india");
    setResidence("total");
  }, []);



  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900">
      <ErrorBoundary>
        <Header
          currentPage={currentPage}
          onNavigate={handleNavigate}
          onHome={handleHome}
        />

        {/*
           Changed from overflow-hidden to allow scrolling.
           Using flex-col to ensure footer stays at bottom if content is short.
        */}
        <main className="flex-1 w-full relative">
          {currentPage === "home" && (
            <ErrorBoundary>
              <Suspense
                fallback={<LoadingFallback text="Loading Home..." />}
              >
                <HomeDashboard />
              </Suspense>
            </ErrorBoundary>
          )}

          {currentPage === "explorer" && (
            <ErrorBoundary>
              <Suspense
                fallback={<LoadingFallback text="Loading Explorer..." />}
              >
                {selectedCategory || selectedIndicator ? (
                  <DataExplorer
                    selectedCategory={selectedCategory}
                    onBack={() => {
                      setSelectedCategory(null);
                      setSelectedIndicator(null);
                    }}
                    selectedIndicator={selectedIndicator}
                    onIndicatorChange={handleIndicatorChange}
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                    geography={geography}
                    onGeographyChange={handleGeographyChange}
                    residence={residence}
                    onResidenceChange={handleResidenceChange}
                  />
                ) : (
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <CategoryGrid
                      onCategorySelect={handleCategorySelect}
                      selectedCategory={null}
                      onSearchSelect={handleSearchSelect}
                    />
                  </div>
                )}
              </Suspense>
            </ErrorBoundary>
          )}

          {currentPage === "cross" && (
            <ErrorBoundary>
              <Suspense
                fallback={<LoadingFallback text="Loading Cross Analysis..." />}
              >
                <CrossAnalysis />
              </Suspense>
            </ErrorBoundary>
          )}

          {currentPage === "reports" && (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <Suspense
                fallback={<LoadingFallback text="Loading Reports..." />}
              >
                <Reports />
              </Suspense>
            </div>
          )}

          {currentPage === "about" && (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <Suspense fallback={<LoadingFallback text="Loading About..." />}>
                <About />
              </Suspense>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="font-bold text-slate-700">TRAC</span>
                <span className="text-slate-300">|</span>
                <span>The Rural Analytic Centre</span>
              </div>

              <div className="text-xs text-slate-400">
                <span>© {new Date().getFullYear()} All Rights Reserved</span>
              </div>
            </div>
          </div>
        </footer>

        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            className: "text-sm font-medium",
            style: {
              background: "#1e293b",
              color: "#fff",
              padding: "12px 16px",
              borderRadius: "8px",
            },
            success: {
              iconTheme: {
                primary: "#0ea5e9",
                secondary: "#fff",
              },
            },
          }}
        />
      </ErrorBoundary>
    </div>
  );
}

export default App;
